import React from "react";
import { useTheme } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
} from "recharts";

import { i18n } from "../../translate/i18n";

const SLICE_COLORS = [
  "#3f51b5",
  "#e91e63",
  "#00acc1",
  "#ff9800",
  "#4caf50",
  "#9c27b0",
  "#795548",
  "#607d8b",
  "#009688",
  "#cddc39",
];

const sumValues = (data) =>
  (Array.isArray(data) ? data : []).reduce(
    (acc, d) => acc + (Number(d.value) || 0),
    0
  );

const TicketReportsCharts = ({ charts, loading }) => {
  const theme = useTheme();
  const textColor = theme.palette.text.secondary;

  const byStatus = Array.isArray(charts?.byStatus) ? charts.byStatus : [];
  const byPhase = Array.isArray(charts?.byPhase) ? charts.byPhase : [];
  const byQueue = Array.isArray(charts?.byQueue) ? charts.byQueue : [];

  const emptyMsg = i18n.t("ticketReports.chartEmpty");

  const tooltipStyle = {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Paper
          style={{ padding: 16, height: 360 }}
          variant="outlined"
          elevation={0}
        >
          <Typography variant="subtitle1" gutterBottom color="textPrimary">
            {i18n.t("ticketReports.chartByStatus")}
          </Typography>
          {sumValues(byStatus) === 0 ? (
            <Typography color="textSecondary" style={{ padding: 40 }}>
              {emptyMsg}
            </Typography>
          ) : (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={byStatus}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={52}
                outerRadius={88}
                paddingAngle={2}
                labelLine={false}
                label={({ name, percent }) =>
                  percent != null && percent > 0.04
                    ? `${String(name).slice(0, 18)}${String(name).length > 18 ? "…" : ""}: ${(percent * 100).toFixed(0)}%`
                    : ""
                }
              >
                {byStatus.map((entry, index) => (
                  <Cell
                    key={`c-s-${entry.name}-${index}`}
                    fill={SLICE_COLORS[index % SLICE_COLORS.length]}
                    stroke={theme.palette.background.paper}
                    strokeWidth={1}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name) => [value, name]}
                contentStyle={tooltipStyle}
              />
              <Legend
                layout="horizontal"
                verticalAlign="bottom"
                wrapperStyle={{ fontSize: 12 }}
              />
            </PieChart>
          </ResponsiveContainer>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper
          style={{ padding: 16, height: 360 }}
          variant="outlined"
          elevation={0}
        >
          <Typography variant="subtitle1" gutterBottom color="textPrimary">
            {i18n.t("ticketReports.chartByPhase")}
          </Typography>
          {sumValues(byPhase) === 0 ? (
            <Typography color="textSecondary" style={{ padding: 40 }}>
              {emptyMsg}
            </Typography>
          ) : (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={byPhase}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={0}
                outerRadius={95}
                paddingAngle={2}
                labelLine
                label={({ percent }) =>
                  percent != null && percent > 0.03
                    ? `${(percent * 100).toFixed(0)}%`
                    : ""
                }
              >
                {byPhase.map((entry, index) => (
                  <Cell
                    key={`c-p-${entry.name}-${index}`}
                    fill={SLICE_COLORS[(index + 3) % SLICE_COLORS.length]}
                    stroke={theme.palette.background.paper}
                    strokeWidth={1}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name) => [value, name]}
                contentStyle={tooltipStyle}
              />
              <Legend
                layout="horizontal"
                verticalAlign="bottom"
                wrapperStyle={{ fontSize: 12 }}
              />
            </PieChart>
          </ResponsiveContainer>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper style={{ padding: 16 }} variant="outlined" elevation={0}>
          <Typography variant="subtitle1" gutterBottom color="textPrimary">
            {i18n.t("ticketReports.chartByQueue")}
          </Typography>
          {sumValues(byQueue) === 0 ? (
            <Typography color="textSecondary" style={{ padding: 24 }}>
              {emptyMsg}
            </Typography>
          ) : (
          <ResponsiveContainer width="100%" height={Math.max(280, byQueue.length * 36)}>
            <BarChart
              data={byQueue}
              layout="vertical"
              margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} stroke={textColor} />
              <YAxis
                type="category"
                dataKey="name"
                width={120}
                tick={{ fontSize: 11 }}
                stroke={textColor}
              />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar
                dataKey="value"
                name={i18n.t("ticketReports.chartVolume")}
                fill={theme.palette.primary.main}
                radius={[0, 4, 4, 0]}
              >
                <LabelList
                  dataKey="value"
                  position="right"
                  style={{ fill: theme.palette.text.primary, fontSize: 12 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default TicketReportsCharts;
